/**
 * Shared course date formatting — extracted (rule-of-three) from near-
 * identical private copies that had drifted across:
 *  - src/components/assistant/content/course-card-full.tsx
 *  - src/components/assistant/content/course-row.tsx
 *  - src/components/assistant/voice/pinned-course-compact.tsx
 *
 * The drift: course-card-full.tsx/course-row.tsx render a SPACED en-dash
 * (" – ") between range endpoints; pinned-course-compact.tsx renders a
 * TIGHT, unspaced one ("–") to fit the compact pin's narrower width. Every
 * range formatter below takes an explicit `separator` (default: spaced) so
 * each caller keeps its own existing, RTL-asserted rendered string
 * byte-identical rather than silently converging on one spacing.
 */

/** Default separator — matches course-card-full.tsx's and course-row.tsx's
 *  original literal. */
export const RANGE_SEPARATOR_SPACED = ' – ';
/** Tighter separator — matches pinned-course-compact.tsx's original
 *  literal (no surrounding spaces). */
export const RANGE_SEPARATOR_TIGHT = '–';

/** "YYYY-MM-DD" → "26 Sep". Splits the string rather than `new Date(str)` —
 *  the latter UTC-parses a date-only string, shifting the day backward in
 *  any negative-UTC-offset timezone (same fix as primitives/date-tile.tsx). */
export function fmtDayMonth(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
}

/** "Sat" / "Mon" — the weekday abbreviation for a "YYYY-MM-DD" date-only
 *  string, parsed the same local-time-safe way as fmtDayMonth. */
export function weekdayOf(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'short',
  });
}

/** "26{sep}28 Sep" for a same-month range, "30 Dec{sep}2 Jan" across a
 *  month/year boundary, or the bare day+month for a single-day course.
 *  `separator` defaults to the spaced en-dash (course-card-full.tsx's
 *  original) — pass RANGE_SEPARATOR_TIGHT to match
 *  pinned-course-compact.tsx's narrower pin. */
export function fmtDateRange(
  start: string,
  end?: string | null,
  separator: string = RANGE_SEPARATOR_SPACED
): string {
  if (!end || end === start) return fmtDayMonth(start);
  const [, startMonth, startDay] = start.split('-').map(Number);
  const [, endMonth] = end.split('-').map(Number);
  if (startMonth === endMonth) {
    return `${startDay}${separator}${fmtDayMonth(end)}`;
  }
  return `${fmtDayMonth(start)}${separator}${fmtDayMonth(end)}`;
}

/** "Sat{sep}Sun" for a multi-day course, or a single weekday for a one-day
 *  one. `start` is optional (returns '') to match course-row.tsx's
 *  undated-course handling; the other two callers always pass a real
 *  start date. `separator` defaults to the spaced en-dash — pass
 *  RANGE_SEPARATOR_TIGHT to match pinned-course-compact.tsx. */
export function fmtWeekdaySpan(
  start?: string | null,
  end?: string | null,
  separator: string = RANGE_SEPARATOR_SPACED
): string {
  if (!start) return '';
  const startWd = weekdayOf(start);
  if (!end || end === start) return startWd;
  return `${startWd}${separator}${weekdayOf(end)}`;
}
